"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api/client";
import { createClient } from "@/lib/supabase/client";

let globalFetchingNotifs = false;
let lastFetchTime = 0;

interface TopbarProps {
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
  pageTitle?: string;
  breadcrumbs?: { label: string; href?: string }[];
}

export default function Topbar({
  sidebarCollapsed,
  onMobileMenuToggle,
  pageTitle,
  breadcrumbs,
}: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [currentDateLabel, setCurrentDateLabel] = useState("");

  useEffect(() => {
    const today = new Date();
    const formatted = today.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit"
    });
    // Remove comma after month day if any, wait, toLocaleDateString usually produces 'Wednesday, August 05, 2026' with this config depending on locale.
    setCurrentDateLabel(formatted);
  }, []);

  const router = useRouter();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.id) {
      setAvatarUrl(user.avatarUrl || localStorage.getItem(`userAvatarUrl_${user.id}`));
    } else {
      setAvatarUrl(null);
    }
    const handleAvatarUpdate = () => {
      if (user?.id) {
        setAvatarUrl(localStorage.getItem(`userAvatarUrl_${user.id}`));
      }
    };
    window.addEventListener("avatar-updated", handleAvatarUpdate);
    return () => window.removeEventListener("avatar-updated", handleAvatarUpdate);
  }, [user?.id]);

  const userRole = user?.roles.includes("admin")
    ? "Administrator"
    : user?.roles.includes("agent")
      ? "Staff"
      : user?.roles.includes("consultant")
        ? "Consultant"
        : "Client";

  const handleLogoutClick = () => {
    setUserMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      const res = await fetch("/api/v1/auth/logout", { method: "POST" });
      if (res.ok) {
        router.push("/sign-up-login-screen");
        router.refresh();
      }
    } catch (err) {
      console.error("Failed to logout", err);
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadedUserRef = useRef<string | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    let channel: any;
    let supabase: any;

    async function loadNotifs() {
      if (!user?.id) return;
      const now = Date.now();
      if (globalFetchingNotifs || (now - lastFetchTime < 1000)) return;
      
      globalFetchingNotifs = true;
      try {
        const { items } = await apiFetch<any>("/api/v1/notifications?view=inbox&limit=50", { auth: true });
        const allUnread = items.filter((n: any) => !n.isRead);
        setNotifications(allUnread.slice(0, 5));
        setUnreadCount(allUnread.length);
        lastFetchTime = Date.now();
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      } finally {
        globalFetchingNotifs = false;
      }
    }

    if (user?.id && loadedUserRef.current !== user.id) {
      loadedUserRef.current = user.id;
      loadNotifs();
    }

    const handleReload = () => loadNotifs();
    window.addEventListener("reload-notifications", handleReload);

    if (process.env.NEXT_PUBLIC_SUPABASE_URL && process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
      try {
        supabase = createClient();
        channel = supabase
          .channel("schema-db-changes")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "notifications",
            },
            () => {
              loadNotifs();
            }
          )
          .subscribe();
      } catch (err) {
        console.error("Failed to initialize Supabase realtime", err);
        // Fallback to interval
        intervalId = setInterval(() => {
          loadNotifs();
        }, 10000);
      }
    } else {
      console.warn("Supabase environment variables missing. Realtime notifications are disabled.");
      intervalId = setInterval(() => {
        loadNotifs();
      }, 10000);
    }

    return () => {
      window.removeEventListener("reload-notifications", handleReload);
      if (intervalId) clearInterval(intervalId);
      if (supabase && channel) supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleMarkAllRead = async () => {
    try {
      await apiFetch("/api/v1/notifications/mark-all-read", { method: "POST", auth: true });
      setNotifications(notifications.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event("reload-notifications"));
    } catch (err) {
      console.error("Failed to mark all as read", err);
    }
  };

  const getNotificationUI = (type: string) => {
    switch (type) {
      case "deadline_reminder":
        return { icon: "ExclamationTriangleIcon", color: "text-red-500" };
      case "renewal_due":
        return { icon: "ArrowPathIcon", color: "text-amber-500" };
      case "task_assigned":
      case "status_change":
        return { icon: "CheckCircleIcon", color: "text-green-500" };
      case "invoice_update":
        return { icon: "BanknotesIcon", color: "text-blue-500" };
      default:
        return { icon: "BellIcon", color: "text-blue-500" };
    }
  };

  return (
    <header
      className={`
          fixed top-0 right-0 h-16 z-30 bg-white border-b border-border
          flex items-center px-4 gap-3
          transition-all duration-300 ease-in-out
          left-0 ${sidebarCollapsed ? "md:left-16" : "md:left-60"}
        `}
    >

      {/* Mobile Menu Toggle */}
      <button
        onClick={onMobileMenuToggle}
        className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
      >
        <Icon name="Bars3Icon" size={20} />
      </button>

      {/* Breadcrumb / Title */}
      <div className="flex-1 min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav className="flex items-center gap-1.5 text-sm min-w-0">
            {breadcrumbs.map((crumb, idx) => (
              <span key={idx} className={`flex items-center gap-1.5 ${idx === breadcrumbs.length - 1 ? "min-w-0" : "flex-shrink-0"}`}>
                {idx > 0 && <Icon name="ChevronRightIcon" size={13} className="text-muted-foreground flex-shrink-0" />}
                {crumb.href && idx < breadcrumbs.length - 1 ? (
                  <Link href={crumb.href} className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={idx === breadcrumbs.length - 1 ? "font-600 text-foreground truncate block" : "text-muted-foreground whitespace-nowrap"}>
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
        ) : (
          <h1 className="text-[15px] font-600 text-foreground truncate">{pageTitle}</h1>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2">
        {/* Date Display */}
        {currentDateLabel && (
          <div className="hidden md:block text-[13px] font-500 text-muted-foreground mr-2 border-r border-border pr-4">
            {currentDateLabel}
          </div>
        )}

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors relative"
            aria-label="Notifications"
          >
            <Icon name="BellIcon" size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-700 rounded-full flex items-center justify-center tabular-nums ring-2 ring-white">
                {unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-modal border border-border z-50 animate-scale-in overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div>
                    <p className="text-sm font-600 text-foreground">Notifications</p>
                    <p className="text-xs text-muted-foreground">{unreadCount} unread</p>
                  </div>
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-primary hover:text-primary/80 font-500 transition-colors"
                  >
                    Mark all read
                  </button>
                </div>
                <ul className="max-h-72 overflow-y-auto scrollbar-thin divide-y divide-border">
                  {notifications.length === 0 ? (
                    <li className="px-4 py-8 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
                      <Icon name="BellSlashIcon" size={24} className="text-muted-foreground/40" />
                      <p>No new notifications</p>
                    </li>
                  ) : (
                    notifications.map((n) => {
                      const ui = getNotificationUI(n.notificationType);
                      return (
                      <li
                        key={n.id}
                        onClick={async () => {
                          if (!n.isRead) {
                            try {
                              await apiFetch(`/api/v1/notifications/${n.id}/read`, { method: "POST", auth: true });
                              setNotifications(notifications.map(x => x.id === n.id ? { ...x, isRead: true } : x));
                              setUnreadCount(prev => Math.max(0, prev - 1));
                              window.dispatchEvent(new Event("reload-notifications-page"));
                            } catch (e) {
                              console.error("Failed to mark read", e);
                            }
                          }
                          if (n.actionUrl) {
                            router.push(n.actionUrl);
                            setNotifOpen(false);
                          }
                        }}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors ${!n.isRead ? "bg-blue-50/40" : ""}`}
                      >
                        <div className={`mt-0.5 flex-shrink-0 ${ui.color}`}>
                          <Icon name={ui.icon as any} size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-[13px] leading-snug ${!n.isRead ? "font-500 text-foreground" : "text-muted-foreground"}`}>
                            {n.message}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {new Date(n.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {!n.isRead && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </li>
                    );
                  })
                  )}
                </ul>
                <div className="px-4 py-2.5 border-t border-border">
                  <Link
                    href="/notifications"
                    onClick={() => setNotifOpen(false)}
                    className="text-xs text-primary hover:text-primary/80 font-500 block w-full text-center transition-colors"
                  >
                    View all notifications
                  </Link>
                </div>
                </div>
            </>
          )}
        </div>

        {/* Last updated */}
        {/* <div className="hidden xl:flex items-center gap-1.5 ml-2 px-2.5 py-1.5 bg-green-50 rounded-lg border border-green-200">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[11px] font-500 text-green-700">Live · Updated 2 min ago</span>
        </div> */}

        {/* User Profile Dropdown */}
        <div className="relative ml-2 pl-2 border-l border-border">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-1 hover:bg-muted p-1 rounded-lg transition-colors focus:outline-none"
          >
            <div className="relative w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] font-700 text-primary">{user?.fullName ? user.fullName.substring(0, 2).toUpperCase() : "U"}</span>
              )}
            </div>
            <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground" />
          </button>

          {userMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-modal border border-border z-50 animate-scale-in overflow-hidden py-1">
                <div className="px-4 py-3 border-b border-border mb-1 flex items-center gap-3">
                  <div className="relative w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[14px] font-700 text-primary">{user?.fullName ? user.fullName.substring(0, 2).toUpperCase() : "U"}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-[13px] font-600 text-foreground">{user?.fullName ?? "User"}</p>
                    <p className="text-[11px] text-muted-foreground">{userRole}</p>
                  </div>
                </div>
                <Link
                  href="/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <Icon name="Cog6ToothIcon" size={16} className="text-muted-foreground" />
                  Settings
                </Link>
                <button
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  onClick={handleLogoutClick}
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={16} />
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !isLoggingOut && setShowLogoutConfirm(false)} />
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-sm mx-4 p-6 relative z-10 animate-scale-in">
            <h3 className="text-lg font-600 text-foreground mb-2">Confirm Logout</h3>
            <p className="text-sm text-muted-foreground mb-6">Are you sure you want to log out of your account?</p>
            <div className="flex gap-3 justify-end">
              <button
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors disabled:opacity-50"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60"
                onClick={handleLogoutConfirm}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Logging out...
                  </>
                ) : (
                  "Logout"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}